import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface WebsiteData {
  domain?: string;
  websiteName?: string;
  websiteDescription?: string;
}

export interface WorkspaceData {
  companyName?: string;
  website?: string;
  industry?: string;
  description?: string;
  country?: string;
  phone?: string;
}

export interface ContentSources {
  blogUrls: string[];
  productUrls: string[];
}

interface OnboardingState {
  // State
  currentStep: number;
  isCompleted: boolean;
  websiteData: WebsiteData; // NEW: Website creation data
  workspaceData: WorkspaceData;
  contentSources: ContentSources;
  selectedPlatforms: string[];

  // Actions
  setStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;

  saveWebsiteData: (data: Partial<WebsiteData>) => void; // NEW
  saveWorkspaceData: (data: Partial<WorkspaceData>) => void;
  saveContentSources: (sources: ContentSources) => void;
  togglePlatform: (platformId: string) => void;

  completeOnboarding: () => void;
  resetOnboarding: () => void;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentStep: 1,
      isCompleted: false,
      websiteData: {}, // NEW
      workspaceData: {},
      contentSources: {
        blogUrls: [],
        productUrls: [],
      },
      selectedPlatforms: [],

      // Navigation
      setStep: (step: number) => {
        if (step >= 1 && step <= 6) { // Updated to 6 steps
          set({ currentStep: step });
        }
      },

      nextStep: () => {
        const { currentStep } = get();
        if (currentStep < 6) { // Updated to 6 steps
          set({ currentStep: currentStep + 1 });
        }
      },

      previousStep: () => {
        const { currentStep } = get();
        if (currentStep > 1) {
          set({ currentStep: currentStep - 1 });
        }
      },

      // Data management
      saveWebsiteData: (data: Partial<WebsiteData>) => { // NEW
        set((state) => ({
          websiteData: {
            ...state.websiteData,
            ...data,
          },
        }));
      },

      saveWorkspaceData: (data: Partial<WorkspaceData>) => {
        set((state) => ({
          workspaceData: {
            ...state.workspaceData,
            ...data,
          },
        }));
      },

      saveContentSources: (sources: ContentSources) => {
        set({ contentSources: sources });
      },

      togglePlatform: (platformId: string) => {
        set((state) => {
          const platforms = state.selectedPlatforms;
          if (platforms.includes(platformId)) {
            return {
              selectedPlatforms: platforms.filter((id) => id !== platformId),
            };
          } else {
            return {
              selectedPlatforms: [...platforms, platformId],
            };
          }
        });
      },

      // Completion
      completeOnboarding: () => {
        set({ isCompleted: true, currentStep: 5 });
      },

      resetOnboarding: () => {
        set({
          currentStep: 1,
          isCompleted: false,
          workspaceData: {},
          contentSources: {
            blogUrls: [],
            productUrls: [],
          },
          selectedPlatforms: [],
        });
      },
    }),
    {
      name: 'onboarding-storage',
      partialize: (state) => ({
        currentStep: state.currentStep,
        isCompleted: state.isCompleted,
        workspaceData: state.workspaceData,
        contentSources: state.contentSources,
        selectedPlatforms: state.selectedPlatforms,
      }),
    }
  )
);
